import React from 'react'
import {Shield, Eye, Cookie, Lock, Mail} from 'lucide-react'

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 via-blue-900 to-blue-950">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-red-600 to-blue-900 py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/10 backdrop-blur-sm p-4 rounded-full">
              <Shield className="w-16 h-16 text-white" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-white/90">
            Your privacy is important to us at TextingSanta.com
          </p>
          <p className="text-sm text-white/70 mt-4">
            Last Updated: January 2025
          </p>
        </div>
      </div>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to TextingSanta.com ("we," "our," or "us"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website textingsanta.com and use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Eye className="w-8 h-8 text-red-600" />
              <h2 className="text-3xl font-bold text-blue-950">Information We Collect</h2>
            </div>
            
            <h3 className="text-xl font-semibold text-blue-900 mt-6 mb-3">Personal Data</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may collect personally identifiable information, such as your:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Child's name and age (for personalization purposes)</li>
              <li>Payment information (processed securely through third-party payment processors)</li>
            </ul>

            <h3 className="text-xl font-semibold text-blue-900 mt-6 mb-3">Usage Data</h3>
            <p className="text-gray-700 leading-relaxed">
              We automatically collect certain information when you visit, use, or navigate our website. This information may include:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring website addresses</li>
              <li>Operating system</li>
            </ul>
          </section>

          {/* Cookies and Tracking */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Cookie className="w-8 h-8 text-red-600" />
              <h2 className="text-3xl font-bold text-blue-950">Cookies and Tracking Technologies</h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track activity on our website and store certain information. Cookies are files with small amounts of data which may include an anonymous unique identifier.
            </p>
            
            <h3 className="text-xl font-semibold text-blue-900 mt-6 mb-3">Types of Cookies We Use:</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Essential Cookies:</strong> Necessary for the website to function properly</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website</li>
              <li><strong>Advertising Cookies:</strong> Used to deliver relevant advertisements to you</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
          </section>

          {/* Google AdSense */}
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Third-Party Advertising</h2>
            <h3 className="text-xl font-semibold text-blue-900 mb-3">Google AdSense</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use Google AdSense to display advertisements on our website. Google AdSense uses cookies to serve ads based on your prior visits to our website or other websites. Google's use of advertising cookies enables it and its partners to serve ads based on your visit to our site and/or other sites on the Internet.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 underline">Google Ads Settings</a> or by visiting <a href="http://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 underline">www.aboutads.info</a>.
            </p>
            <p className="text-gray-700 leading-relaxed">
              For more information about Google's privacy practices, please review the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-red-700 underline">Google Privacy Policy</a>.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-3xl font-bold text-blue-950 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect in the following ways:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>To provide, operate, and maintain our website and services</li>
              <li>To send personalized messages from Santa</li>
              <li>To process your transactions and manage your orders</li>
              <li>To improve, personalize, and expand our website</li>
              <li>To understand and analyze how you use our website</li>
              <li>To develop new products, services, features, and functionality</li>
              <li>To communicate with you for customer service and support</li>
              <li>To send you updates, promotional materials, and marketing communications</li>
              <li>To detect, prevent, and address technical issues and fraudulent activity</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-center space-x-3 mb-4">
              <Lock className="w-8 h-8 text-red-600" />
              <h2 className="text-3xl font-bold text-blue-950">Data Security</h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information. However, please note that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-red-50 rounded-lg p-6">
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our service is intended for use by parents and guardians to create magical experiences for children. We do not knowingly collect personally identifiable information directly from children under 13 without parental consent. If you are a parent or guardian and you are aware that your child has provided us with personal information without your consent, please contact us.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Parents and guardians provide information about their children (such as name and age) to personalize Santa messages. This information is used solely for the purpose of providing our services and is protected in accordance with this Privacy Policy.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites, including the NORAD Santa Tracker and Google Santa Tracker. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          {/* Your Privacy Rights */}
          <section>
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Your Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The right to access your personal data</li>
              <li>The right to rectification of inaccurate data</li>
              <li>The right to erasure ("right to be forgotten")</li>
              <li>The right to restrict processing</li>
              <li>The right to data portability</li>
              <li>The right to object to processing</li>
              <li>The right to withdraw consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise any of these rights, please contact us using the information provided below.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section className="bg-blue-50 rounded-lg p-6">
            <h2 className="text-3xl font-bold text-blue-950 mb-4">California Privacy Rights (CCPA)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you are a California resident, you have specific rights regarding your personal information under the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>Right to know whether personal information is sold or disclosed</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to deletion of personal information</li>
              <li>Right to non-discrimination for exercising CCPA rights</li>
            </ul>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-3xl font-bold text-blue-950 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-gradient-to-r from-red-600 to-blue-900 rounded-lg p-6 text-white">
            <div className="flex items-center space-x-3 mb-4">
              <Mail className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Contact Us</h2>
            </div>
            <p className="leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="space-y-2">
              <p><strong>Email:</strong> privacy@textingsanta.com</p>
              <p><strong>Website:</strong> textingsanta.com/contact</p>
              <p><strong>Mail:</strong> Texting Santa, North Pole Communications, Arctic Circle</p>
            </div>
          </section>

          {/* Consent */}
          <section className="border-t-2 border-blue-200 pt-6">
            <p className="text-gray-700 leading-relaxed">
              By using our website and services, you consent to our Privacy Policy and agree to its terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
